/**
 * Carousel Slider Component
 * Plain JS component for Swiper-based carousels
 */
import Swiper from "swiper";
import { Autoplay, EffectFade, Keyboard, Navigation, Pagination } from "swiper/modules";
import { devLogger } from "@/utils/dev-logger.util";
import { LoadingImageComponent } from "../LoadingImage";
const getConfiguration = (element, props) => {
    const getAttr = (name, def) => element.getAttribute(name) || def;
    const getBool = (name) => element.getAttribute(name) === "true";
    const getInt = (name, def) => parseInt(getAttr(name, def), 10);
    return {
        autoplay: props?.autoplay ?? getBool("data-autoplay"),
        autoplaySpeed: props?.autoplaySpeed || getInt("data-autoplay-speed", "5000"),
        showArrows: props?.showArrows ?? getBool("data-show-arrows"),
        showDots: props?.showDots ?? getBool("data-show-dots"),
        infinite: props?.infinite ?? getBool("data-infinite"),
        slidesToShowMobile: props?.slidesToShowMobile || getInt("data-slides-mobile", "1"),
        slidesToShowDesktop: props?.slidesToShowDesktop || getInt("data-slides-desktop", "3"),
        disableSlideOnDesktop: props?.disableSlideOnDesktop ?? getBool("data-disable-desktop"),
        pauseOnHover: props?.pauseOnHover ?? true,
        spaceBetween: props?.spaceBetween ?? getInt("data-space-between", "20"),
        spaceBetweenMobile: props?.spaceBetweenMobile ?? getInt("data-space-between-mobile", "10"),
        breakpoint: props?.breakpoint ?? 992,
        carouselNavigation: props?.carouselNavigation ?? getAttr("data-carousel-navigation", ""),
        carouselPagination: props?.carouselPagination ?? getAttr("data-carousel-pagination", ""),
        centerMode: props?.centerMode ?? getBool("data-center-mode"),
        rightPreviewMode: props?.rightPreviewMode ?? getBool("data-right-preview-mode"),
        leftRightPreviewMode: props?.leftRightPreviewMode ?? getBool("data-left-right-preview-mode"),
        showDesktopCounterNav: props?.showDesktopCounterNav ?? getBool("data-show-desktop-counter-nav"),
        effect: (props?.effect ?? getAttr("data-effect", "slide")),
        badgeLabels: props?.badgeLabels ?? getAttr("data-badge-labels", "").split(","),
    };
};
const setupLazyLoading = (swiperContainer, swiper, rightPreviewMode) => {
    if (!rightPreviewMode)
        return () => { };
    const lazyPictures = swiperContainer.querySelectorAll(".lazy-preview:not(.lazy-loaded)");
    if (lazyPictures.length === 0) {
        return () => { };
    }
    let imagesLoaded = false;
    const loadLazyImage = (pictureElement) => {
        if (pictureElement.classList.contains("lazy-loaded")) {
            return;
        }
        const srcMobile = pictureElement.getAttribute("src-mobile");
        const srcDesktop = pictureElement.getAttribute("src-desktop");
        if (!srcMobile || !srcDesktop) {
            return;
        }
        const imgMobile = srcMobile + "?width=450";
        const imgDesktop = srcDesktop + "?width=1920";
        const sources = pictureElement.querySelectorAll("source");
        const img = pictureElement.querySelector("img");
        if (sources.length >= 2) {
            sources[0].setAttribute("srcset", imgMobile);
            sources[1].setAttribute("srcset", imgDesktop);
        }
        if (img) {
            img.src = imgDesktop;
            img.loading = "lazy";
        }
        pictureElement.classList.add("lazy-loaded");
    };
    const loadAllLazyImages = () => {
        if (imagesLoaded)
            return;
        imagesLoaded = true;
        lazyPictures.forEach((picture) => {
            if (!picture.classList.contains("lazy-loaded")) {
                loadLazyImage(picture);
            }
        });
        cleanupInteractionListeners();
    };
    const interactionEvents = [
        "mousemove",
        "touchstart",
        "keydown",
        "scroll",
        "click",
        "focusin",
    ];
    const interactionOptions = { passive: true, once: true };
    const handleInteraction = () => loadAllLazyImages();
    interactionEvents.forEach((eventType) => {
        document.addEventListener(eventType, handleInteraction, interactionOptions);
    });
    const cleanupInteractionListeners = () => {
        interactionEvents.forEach((eventType) => {
            document.removeEventListener(eventType, handleInteraction, interactionOptions);
        });
    };
    const handleSlideChange = () => {
        const activeSlide = swiperContainer.querySelector(".swiper-slide-active");
        if (activeSlide) {
            const activePicture = activeSlide.querySelector(".lazy-preview:not(.lazy-loaded)");
            if (activePicture) {
                loadLazyImage(activePicture);
            }
        }
    };
    swiper.on("slideChange", handleSlideChange);
    return () => {
        cleanupInteractionListeners();
        swiper.off("slideChange", handleSlideChange);
    };
};
const cloneIndexedTemplates = (root, sourceSelector, templateSelector, indexAttribute) => {
    const source = root.querySelector(sourceSelector);
    if (!source) {
        return [];
    }
    const templates = source.querySelectorAll(templateSelector);
    const cloned = Array.from(templates)
        .map((template) => template.cloneNode(true))
        .sort((a, b) => {
        const indexA = parseInt(a.getAttribute(indexAttribute) || "0", 10);
        const indexB = parseInt(b.getAttribute(indexAttribute) || "0", 10);
        return indexA - indexB;
    });
    source.remove();
    return cloned;
};
const TAB_FADE_SIZE = "2.5rem";
const TAB_FADE_NONE = "0px";
const parseBulletLabels = (raw) => {
    if (!raw) {
        return [];
    }
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => (item == null ? "" : String(item)));
            }
        }
        catch {
            // Fall through to legacy comma-separated values.
        }
    }
    return trimmed.split(",");
};
const setupLrDesktopNav = (element, swiper, options) => {
    if (!options.leftRightPreviewMode || !options.showDesktopCounterNav) {
        return () => { };
    }
    const tabsScroll = element.querySelector("[data-lr-tabs-scroll]");
    const pagination = element.querySelector(".carousel-swiper-pagination");
    const slideCounter = element.querySelector("[data-slide-counter]");
    const slideCount = parseInt(element.querySelector(".swiper")?.getAttribute("data-slide-count") || "0", 10);
    const isDesktop = () => window.matchMedia(`(min-width: ${options.desktopBreakpoint}px)`).matches;
    const getSlideTotal = () => slideCount ||
        swiper.slides.filter((slide) => !slide.classList.contains("swiper-slide-duplicate")).length;
    const updateNav = () => {
        if (!isDesktop()) {
            tabsScroll?.removeAttribute("data-tabs-overflow");
            return;
        }
        const index = swiper.realIndex;
        const total = getSlideTotal();
        if (slideCounter) {
            slideCounter.textContent = `${index + 1}/${total}`;
        }
        if (!tabsScroll || !pagination) {
            return;
        }
        const overflows = pagination.scrollWidth > tabsScroll.clientWidth;
        tabsScroll.dataset.tabsOverflow = overflows ? "true" : "false";
        if (!overflows) {
            tabsScroll.scrollLeft = 0;
            tabsScroll.style.setProperty("--tabs-fade-start", TAB_FADE_NONE);
            tabsScroll.style.setProperty("--tabs-fade-end", TAB_FADE_NONE);
            return;
        }
        const activeTab = pagination.querySelector(".carousel-swiper-pagination-bullet-active");
        if (activeTab) {
            const maxScroll = tabsScroll.scrollWidth - tabsScroll.clientWidth;
            const targetScroll = Math.min(maxScroll, Math.max(0, activeTab.offsetLeft - (tabsScroll.clientWidth - activeTab.offsetWidth) / 2));
            tabsScroll.scrollTo({ left: targetScroll, behavior: "smooth" });
        }
        tabsScroll.style.setProperty("--tabs-fade-start", index === 0 ? TAB_FADE_NONE : TAB_FADE_SIZE);
        tabsScroll.style.setProperty("--tabs-fade-end", index === total - 1 ? TAB_FADE_NONE : TAB_FADE_SIZE);
    };
    updateNav();
    swiper.on("paginationUpdate", updateNav);
    window.addEventListener("resize", updateNav);
    return () => {
        window.removeEventListener("resize", updateNav);
        swiper.off("paginationUpdate", updateNav);
    };
};
export function CarouselSlider(element, props) {
    if (element.getAttribute("data-disable-swiper") === "true") {
        devLogger.log("⚠️ CarouselSlider: Swiper disabled (data-disable-swiper='true')");
        return () => { };
    }
    const config = getConfiguration(element, props);
    // Support both internal and external navigation/pagination via CSS selectors
    let carouselNavContainer = element;
    let carouselPaginationContainer = element;
    if (config.carouselNavigation) {
        // If it's a selector (starts with # or .), use document.querySelector for external elements
        const externalNav = config.carouselNavigation.startsWith("#") || config.carouselNavigation.startsWith(".")
            ? document.querySelector(config.carouselNavigation)
            : element.querySelector(`.${config.carouselNavigation}`);
        if (externalNav)
            carouselNavContainer = externalNav;
    }
    if (config.carouselPagination) {
        const externalPagination = config.carouselPagination.startsWith("#") || config.carouselPagination.startsWith(".")
            ? document.querySelector(config.carouselPagination)
            : element.querySelector(`.${config.carouselPagination}`);
        if (externalPagination)
            carouselPaginationContainer = externalPagination;
    }
    let swiperContainer = element.querySelector(".swiper");
    if (config.rightPreviewMode) {
        swiperContainer = element.querySelector(".swiper-main-content");
    }
    const navigationPrev = carouselNavContainer.querySelector(".carousel-swiper-button-prev");
    const navigationNext = carouselNavContainer.querySelector(".carousel-swiper-button-next");
    const paginationContainer = carouselPaginationContainer.querySelector(".carousel-swiper-pagination");
    const leftPreview = element.querySelector("[data-left-preview]");
    const rightPreview = element.querySelector("[data-right-preview]");
    const bulletLabels = parseBulletLabels(element.querySelector("[data-bullet-labels]")?.getAttribute("data-bullet-labels"));
    if (!swiperContainer || !swiperContainer.querySelector(".swiper-wrapper")) {
        devLogger.error("[CarouselSlider] Missing .swiper or .swiper-wrapper");
        return () => { };
    }
    const lrPreviewLoadingCleanup = new Map();
    const refreshPreviewLazyImages = (previewEl) => {
        const existingCleanup = lrPreviewLoadingCleanup.get(previewEl);
        existingCleanup?.();
        lrPreviewLoadingCleanup.set(previewEl, LoadingImageComponent(previewEl));
    };
    // Clone left/right preview templates (Image Text carousel)
    let clonedLrPreviewTemplates = [];
    if (config.leftRightPreviewMode) {
        clonedLrPreviewTemplates = cloneIndexedTemplates(element, "#list-lr-preview-templates", ".lr-preview-template[data-preview-index]", "data-preview-index");
        if (clonedLrPreviewTemplates.length > 0) {
            devLogger.log(`[CarouselSlider] Cloned ${clonedLrPreviewTemplates.length} left/right preview templates`);
        }
    }
    const updateLeftRightPreview = (realIndex) => {
        if (!config.leftRightPreviewMode || clonedLrPreviewTemplates.length === 0) {
            return;
        }
        const total = clonedLrPreviewTemplates.length;
        const leftIndex = (realIndex - 1 + total) % total;
        const rightIndex = (realIndex + 1) % total;
        const applyTemplate = (previewEl, templateIndex, labelIndex, labelFallbackIndex) => {
            if (!previewEl) {
                return;
            }
            const previewContent = previewEl.querySelector("[data-content]");
            const previewLabel = previewEl.querySelector("[data-label]");
            const previewBadge = previewEl.querySelector("[data-badge]");
            const template = clonedLrPreviewTemplates[templateIndex];
            const imagePart = template?.querySelector(".lr-preview-image");
            if (previewContent) {
                previewContent.innerHTML = imagePart?.innerHTML ?? "";
            }
            if (previewLabel) {
                previewLabel.innerHTML =
                    bulletLabels?.[labelIndex] || bulletLabels?.[labelFallbackIndex] || "";
            }
            if (previewBadge) {
                previewBadge.innerHTML =
                    config.badgeLabels?.[labelIndex] ||
                        config.badgeLabels?.[labelFallbackIndex] ||
                        "";
            }
            refreshPreviewLazyImages(previewEl);
        };
        applyTemplate(leftPreview, leftIndex, realIndex - 1, (bulletLabels?.length ?? 1) - 1);
        applyTemplate(rightPreview, rightIndex, realIndex + 1, 0);
    };
    let clonedThumbnailTemplates = [];
    if (config.rightPreviewMode) {
        clonedThumbnailTemplates = cloneIndexedTemplates(element, "#list-thumbs-hero-banner", ".thumb-template[data-thumb-index]", "data-thumb-index");
        if (clonedThumbnailTemplates.length > 0) {
            devLogger.log(`[CarouselSlider] Cloned ${clonedThumbnailTemplates.length} thumbnail templates and removed source container`);
        }
    }
    const updateThumbsContent = (activeIndex, shouldAnimate = true) => {
        // Only update thumbs content in rightPreviewMode
        if (!config.rightPreviewMode) {
            return;
        }
        const listItems = element.querySelectorAll(".list-img .list-img-item");
        // Use cloned templates (should always be available in rightPreviewMode)
        if (clonedThumbnailTemplates.length === 0) {
            devLogger.log("[CarouselSlider] No cloned thumbnail templates available");
            return;
        }
        const thumbnailTemplates = clonedThumbnailTemplates;
        if (listItems.length === 0) {
            devLogger.log("[CarouselSlider] No list-img-item elements found");
            return;
        }
        const total = thumbnailTemplates.length;
        listItems.forEach((item, i) => {
            const nextIndex = (activeIndex + i + 1) % total;
            const thumbnailTemplate = thumbnailTemplates[nextIndex];
            if (!thumbnailTemplate) {
                devLogger.log(`[CarouselSlider] Thumbnail template not found at index ${nextIndex}`);
                return;
            }
            item.innerHTML = thumbnailTemplate.innerHTML;
            if (shouldAnimate) {
                item.classList.remove("animate-1", "animate-2", "animate-3");
                void item.offsetWidth;
                item.classList.add(`animate-${i + 1}`);
            }
            else {
                item.classList.remove("animate-1", "animate-2", "animate-3");
            }
            if (i === 0) {
                item.classList.add("list-img-item--active");
            }
            else {
                item.classList.remove("list-img-item--active");
            }
            item.onclick = (e) => {
                const target = e.target;
                if (target.closest('a.slide-thumbnail-explore')) {
                    return;
                }
                if (config.infinite) {
                    swiper.slideToLoop(nextIndex);
                }
                else {
                    swiper.slideTo(nextIndex);
                }
            };
        });
    };
    const swiperOptions = {
        modules: [Navigation, Pagination, Autoplay, Keyboard, EffectFade],
        speed: 500,
        loop: config.infinite,
        effect: config.effect || "slide",
        fadeEffect: {
            crossFade: true,
        },
        spaceBetween: config.spaceBetweenMobile,
        slidesPerView: config.slidesToShowMobile,
        watchOverflow: true,
        keyboard: {
            enabled: true,
            onlyInViewport: true,
        },
        on: {
            slideChangeTransitionStart: (swiper) => {
                if (config.rightPreviewMode) {
                    updateThumbsContent(swiper.realIndex, true);
                }
                swiper.el.setAttribute("data-changed", "");
                if (config.centerMode) {
                    const realIndex = swiper.realIndex;
                    if (config.leftRightPreviewMode) {
                        updateLeftRightPreview(realIndex);
                    }
                    else {
                        const activeIndex = swiper.activeIndex;
                        const previousSlideEl = swiper.slides[activeIndex - 1] || swiper.slides[swiper.slides.length - 1];
                        const nextSlideEl = swiper.slides[activeIndex + 1] || swiper.slides[0];
                        if (leftPreview) {
                            const leftPreviewContent = leftPreview.querySelector("[data-content]");
                            const leftPreviewLabel = leftPreview.querySelector("[data-label]");
                            const leftPreviewBadge = leftPreview.querySelector("[data-badge]");
                            if (leftPreviewContent) {
                                leftPreviewContent.innerHTML = previousSlideEl?.innerHTML || "";
                            }
                            if (leftPreviewLabel) {
                                leftPreviewLabel.innerHTML =
                                    bulletLabels?.[realIndex - 1] || bulletLabels?.[bulletLabels.length - 1] || "";
                            }
                            if (leftPreviewBadge) {
                                leftPreviewBadge.innerHTML =
                                    config.badgeLabels?.[realIndex - 1] ||
                                        config.badgeLabels?.[config.badgeLabels.length - 1] ||
                                        "";
                            }
                        }
                        if (rightPreview) {
                            const rightPreviewContent = rightPreview.querySelector("[data-content]");
                            const rightPreviewLabel = rightPreview.querySelector("[data-label]");
                            const rightPreviewBadge = rightPreview.querySelector("[data-badge]");
                            if (rightPreviewContent) {
                                rightPreviewContent.innerHTML = nextSlideEl?.innerHTML || "";
                            }
                            if (rightPreviewLabel) {
                                rightPreviewLabel.innerHTML =
                                    bulletLabels?.[realIndex + 1] || bulletLabels?.[0] || "";
                            }
                            if (rightPreviewBadge) {
                                rightPreviewBadge.innerHTML =
                                    config.badgeLabels?.[realIndex + 1] || config.badgeLabels?.[0] || "";
                            }
                        }
                        LoadingImageComponent(leftPreview);
                        LoadingImageComponent(rightPreview);
                    }
                }
            },
            init: (swiper) => {
                if (config.rightPreviewMode) {
                    updateThumbsContent(swiper.realIndex, false);
                }
                if (config.leftRightPreviewMode) {
                    updateLeftRightPreview(swiper.realIndex);
                }
            },
        },
        breakpoints: {
            [config.breakpoint]: {
                slidesPerView: config.slidesToShowDesktop,
                slidesPerGroup: config.slidesToShowDesktop,
                spaceBetween: config.spaceBetween,
                enabled: !config.disableSlideOnDesktop,
                ...(config.centerMode
                    ? {
                        speed: 0,
                        effect: "fade",
                        pagination: {
                            el: paginationContainer,
                            clickable: true,
                            bulletClass: "carousel-swiper-pagination-bullet",
                            bulletActiveClass: "carousel-swiper-pagination-bullet-active",
                            lockClass: "swiper-pagination-lock",
                            renderBullet: (index, className) => {
                                const label = bulletLabels?.[index] || "";
                                const labelHtml = label
                                    ? `<span class="hidden lg:block whitespace-nowrap">${label}</span>`
                                    : "";
                                return `<span class="${className}">${labelHtml}</span>`;
                            },
                        },
                    }
                    : {}),
            },
        },
        navigation: config.showArrows && navigationPrev && navigationNext
            ? {
                prevEl: navigationPrev,
                nextEl: navigationNext,
                disabledClass: "swiper-button-disabled",
                lockClass: "swiper-button-lock",
            }
            : false,
        pagination: config.showDots && paginationContainer
            ? {
                el: paginationContainer,
                clickable: true,
                bulletClass: "carousel-swiper-pagination-bullet",
                bulletActiveClass: "carousel-swiper-pagination-bullet-active",
                lockClass: "swiper-pagination-lock",
            }
            : false,
        autoplay: config.autoplay && config.autoplaySpeed > 0
            ? {
                delay: config.autoplaySpeed,
                disableOnInteraction: false,
                pauseOnMouseEnter: config.pauseOnHover,
            }
            : false,
    };
    const swiper = new Swiper(swiperContainer, swiperOptions);
    const cleanupLrDesktopNav = setupLrDesktopNav(element, swiper, {
        leftRightPreviewMode: config.leftRightPreviewMode,
        showDesktopCounterNav: config.showDesktopCounterNav,
        desktopBreakpoint: config.breakpoint,
    });
    const cleanupLazyLoading = setupLazyLoading(swiperContainer, swiper, config.rightPreviewMode);
    if (leftPreview) {
        leftPreview.addEventListener("click", () => {
            swiper.slidePrev();
        });
    }
    if (rightPreview) {
        rightPreview.addEventListener("click", () => {
            swiper.slideNext();
        });
    }
    return () => {
        cleanupLrDesktopNav();
        cleanupLazyLoading();
        lrPreviewLoadingCleanup.forEach((cleanup) => cleanup());
        lrPreviewLoadingCleanup.clear();
        if (swiper) {
            swiper.destroy(true, true);
        }
    };
}
